/**
 * Nested T-Hangar Generate Functions — Interleaving/Zipper Pattern
 *
 * Row A: N units all facing south (rotation=0), offset_y=0
 * Row B: N-1 units all facing north (rotation=PI), offset_y=wing_depth, shifted pitch/2 in X
 */

import type { GeneratedBuilding, GeneratedUnit, DoorSpec } from '../types'
import type { NestedTHangarParams } from './params'
import { calculateNestedUnitPitch, createDefaultTHangarDoorSpec } from './params'
import type { THangarParams } from '../standard-t-hangar/params'
import {
  calculateBuildingDepth,
  calculateBuildingWidth,
  calculateRowAOffsets,
  calculateRowBOffsets,
  getRowBCount,
  calculateEndSpaceDimensions,
} from './calculations'
import { createNestedTHangarEdges } from './edges'

/**
 * Build a minimal THangarParams for edge creation (they expect the standard interface)
 */
function toTHangarParams(params: NestedTHangarParams, unitCount: number): THangarParams {
  return {
    name: params.name,
    code: params.code,
    latitude: params.latitude,
    longitude: params.longitude,
    bearing_deg: params.bearing_deg,
    floor_color: params.floor_color,
    wall_color: params.wall_color,
    unit_width_ft: params.unit_width_ft,
    wing_depth_ft: params.wing_depth_ft,
    tail_width_ft: params.tail_width_ft,
    height_ft: params.height_ft,
    unit_count: unitCount,
    default_door_type: params.default_door_type,
    default_door_width_ft: params.default_door_width_ft,
    default_door_height_ft: params.default_door_height_ft,
    wall_thickness_ft: params.wall_thickness_ft,
    unit_overrides: [],
  }
}

/**
 * Generate building configuration
 */
export function generateBuilding(params: NestedTHangarParams): GeneratedBuilding {
  const rotationRadians = (params.bearing_deg * Math.PI) / 180
  const buildingWidth = calculateBuildingWidth(params)
  const buildingDepth = calculateBuildingDepth(params)
  const unitPitch = calculateNestedUnitPitch(params)

  return {
    name: params.name,
    code: params.code,
    type: 'nested_t_hangar_row',
    exterior_width: buildingWidth,
    exterior_depth: buildingDepth,
    exterior_height: params.height_ft,
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: Math.min(params.default_door_width_ft, params.unit_width_ft - 4),
    door_height: params.default_door_height_ft,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      doors: [],
    },
    type_config: {
      template: 'nested_t_hangar',
      unit_count: params.unit_count,
      unit_width_ft: params.unit_width_ft,
      wing_depth_ft: params.wing_depth_ft,
      tail_depth_ft: params.tail_depth_ft,
      tail_width_ft: params.tail_width_ft,
      unit_pitch_ft: unitPitch,
      wall_thickness_ft: params.wall_thickness_ft,
      left_end_mode: params.left_end_mode,
      right_end_mode: params.right_end_mode,
      default_door_type: params.default_door_type,
      default_door_width_ft: params.default_door_width_ft,
      default_door_height_ft: params.default_door_height_ft,
      row_a_overrides: params.row_a_overrides?.length ? params.row_a_overrides : undefined,
      row_b_overrides: params.row_b_overrides?.length ? params.row_b_overrides : undefined,
    },
  }
}

/**
 * Generate units for both rows
 */
export function generateUnits(
  _building: GeneratedBuilding,
  params: NestedTHangarParams
): GeneratedUnit[] {
  const units: GeneratedUnit[] = []
  const rowBCount = getRowBCount(params)

  const thParams = toTHangarParams(params, params.unit_count)
  const defaultDoorSpec = createDefaultTHangarDoorSpec(thParams)

  // Interior depth per unit = wing_depth + tail_depth (wing bay + tail bay)
  const tailDepth = params.tail_depth_ft ?? params.wing_depth_ft
  const interiorDepth = params.wing_depth_ft + tailDepth

  // ── Row A: N units, ALL south-facing, offset_y = 0 ──────────────────
  const rowAOffsets = calculateRowAOffsets(params)

  for (let i = 0; i < params.unit_count; i++) {
    const unitCode = `${params.code}-A-${String(i + 1).padStart(2, '0')}`
    const unitOverride = params.row_a_overrides?.find(o => o.index === i)
    const doorSpec: DoorSpec = unitOverride?.doors?.front?.[0] ?? defaultDoorSpec

    const tHangarEdges = createNestedTHangarEdges(
      i, params.unit_count, thParams, `${params.code}-A`, doorSpec,
      params.tail_depth_ft
    )

    units.push({
      code: unitCode,
      type: unitOverride?.type || 't_hangar',
      capacity_strategy: 'single',
      interior_width: params.unit_width_ft,
      interior_depth: interiorDepth,
      interior_height: params.height_ft,
      door_width: doorSpec.width_ft,
      door_height: doorSpec.height_ft,
      door_type: doorSpec.type,
      offset_x: rowAOffsets[i],
      offset_y: 0, // Row A at south
      unit_rotation: 0, // ALL south-facing
      edges: undefined,
      tHangarEdges,
    })
  }

  // ── Row B: N-1 units, ALL north-facing, offset_y = wing_depth ──────
  const rowBOffsets = calculateRowBOffsets(params)
  const thParamsB = toTHangarParams(params, rowBCount)
  const endSpace = calculateEndSpaceDimensions(params)
  const leftEndMode = params.left_end_mode || 'separate'
  const rightEndMode = params.right_end_mode || 'separate'

  for (let j = 0; j < rowBCount; j++) {
    const unitCode = `${params.code}-B-${String(j + 1).padStart(2, '0')}`
    const unitOverrideB = params.row_b_overrides?.find(o => o.index === j)
    const doorSpec: DoorSpec = unitOverrideB?.doors?.front?.[0] ?? defaultDoorSpec

    const tHangarEdges = createNestedTHangarEdges(
      j, rowBCount, thParamsB, `${params.code}-B`, doorSpec,
      params.tail_depth_ft
    )

    const unitOffsetX = rowBOffsets[j]
    const unitInteriorWidth = params.unit_width_ft

    units.push({
      code: unitCode,
      type: unitOverrideB?.type || 't_hangar',
      capacity_strategy: 'single',
      interior_width: unitInteriorWidth,
      interior_depth: interiorDepth,
      interior_height: params.height_ft,
      door_width: doorSpec.width_ft,
      door_height: doorSpec.height_ft,
      door_type: doorSpec.type,
      offset_x: unitOffsetX,
      offset_y: params.wing_depth_ft, // Row B shifted north by wing_depth
      unit_rotation: Math.PI, // ALL north-facing
      edges: undefined,
      tHangarEdges,
    })
  }

  // ── merge_row_a: A edge units keep standard dimensions ──────────────
  // Back wall suppression is handled in the orchestrator at render time
  // (generate.ts mutations don't persist after save/reload from DB).

  // ── Separate mode per side: add END box units (no edges — orchestrator renders walls) ──
  if (leftEndMode === 'separate') {
    const endLOffsetX = params.wall_thickness_ft
    const endLCode = `${params.code}-END-L`
    units.push({
      code: endLCode,
      type: 'box_hangar',
      capacity_strategy: 'single',
      interior_width: endSpace.width,
      interior_depth: endSpace.depth,
      interior_height: params.height_ft,
      door_width: 0,
      door_height: 0,
      door_type: 'rolling',
      offset_x: endLOffsetX,
      offset_y: params.wing_depth_ft + tailDepth + params.wing_depth_ft / 2, // Centered in Row B wing zone
      unit_rotation: 0,
      edges: undefined,
      tHangarEdges: undefined,
    })
  }

  if (rightEndMode === 'separate') {
    const lastBRight = rowBOffsets[rowBCount - 1] + params.unit_width_ft + params.wall_thickness_ft
    const endRCode = `${params.code}-END-R`
    units.push({
      code: endRCode,
      type: 'box_hangar',
      capacity_strategy: 'single',
      interior_width: endSpace.width,
      interior_depth: endSpace.depth,
      interior_height: params.height_ft,
      door_width: 0,
      door_height: 0,
      door_type: 'rolling',
      offset_x: lastBRight,
      offset_y: params.wing_depth_ft + tailDepth + params.wing_depth_ft / 2, // Centered in Row B wing zone
      unit_rotation: 0,
      edges: undefined,
      tHangarEdges: undefined,
    })
  }

  return units
}
